import * as React from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker as BasicDatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker as BasicDateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Dayjs } from 'dayjs';
import { Stack, styled, SxProps, Theme } from '@mui/material';
import FormLabel from '@mui/material/FormLabel';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import { TextError } from './TextError';

export const FILTER_DATE_MIN_WIDTH = 220;

type DatePickerProps = {
  minDate?: Dayjs;
  maxDate?: Dayjs;
  value?: Dayjs | null;
  title?: string;
  disabled?: boolean;
  onChange?: (val: Dayjs | null) => void;
  inputError?: string;
  style?: React.CSSProperties;
  titleStyle?: React.CSSProperties;
  dateStyle?: SxProps<Theme>;
  /** When true, shows date and time controls. */
  includeTime?: boolean;
};

/**
 *
 * @param value
 * @param onChange
 * @param title
 * @param inputError
 * @param minDate
 * @param maxDate
 * @param style
 * @param titleStyle
 * @constructor
 */
export const DatePicker = ({
  value,
  onChange,
  title,
  inputError,
  minDate,
  maxDate,
  disabled,
  style = { flexDirection: 'column', width: '100%' },
  titleStyle,
  dateStyle,
  includeTime = false
}: DatePickerProps) => {
  const pickerSx: SxProps<Theme> =
    dateStyle == null
      ? { width: '100%', minWidth: FILTER_DATE_MIN_WIDTH }
      : [
          { width: '100%', minWidth: FILTER_DATE_MIN_WIDTH },
          ...(Array.isArray(dateStyle) ? dateStyle : [dateStyle])
        ];

  const commonProps = {
    disabled,
    minDate,
    maxDate,
    sx: pickerSx,
    value: value ?? null,
    onChange,
    slotProps: {
      actionBar: {
        actions: ['clear' as const]
      },
      textField: {
        error: !!inputError
      }
    }
  };

  return (
    <Stack flexDirection="column" width="100%" style={style}>
      <StyleLocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack width="100%" sx={{ minWidth: 0 }}>
          {title && (
            <FormLabel>
              <Typography mb={'9px'} fontWeight={400} color={'#818181'} style={titleStyle}>
                {title}
              </Typography>
            </FormLabel>
          )}
          {includeTime ? (
            <StyledDateTimePicker {...commonProps} ampm={false} />
          ) : (
            <StyledDatePicker {...commonProps} error={!!inputError} />
          )}
          {inputError && (
            <FormHelperText error={!!inputError} sx={{ ml: 0, pt: 0.5, maxWidth: '100%' }}>
              <TextError errorText={inputError} />
            </FormHelperText>
          )}
        </Stack>
      </StyleLocalizationProvider>
    </Stack>
  );
};

const StyledDatePicker = styled(BasicDatePicker)<{ error?: boolean }>(({ error }) => ({
  width: '100%',
  '& .MuiInputBase-root': {
    border: error ? '0.2px solid red' : undefined,
    borderRadius: 10
  }
}));

const StyledDateTimePicker = styled(BasicDateTimePicker)({
  width: '100%',
  '& .MuiInputBase-root': {
    borderRadius: 10
  }
});

const StyleLocalizationProvider = styled(LocalizationProvider)(() => ({
  width: '100%',
  gap: '10px'
}));
